/**
 * Alpha OPS early-access form. Signs the trader up through
 * `/product-launch/subscribe/` as `alpha_ops` (see productLaunchApi). The page
 * owns the "joined" state.
 */
import { useId, useState } from 'react';
import {
  subscribeErrorMessage,
  useSubscribeProductLaunchMutation,
} from '../../store/api/productLaunchApi';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function JoinedNote({ children }) {
  return (
    <p className="aops-wait-done" role="status">
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="m3.5 8.5 3 3 6-7"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {children}
    </p>
  );
}

export default function WaitlistForm({
  disabled = false,
  submitLabel = 'Join the waitlist',
  busyLabel = 'Joining…',
  note = 'Get notified when Alpha OPS launches.',
  placeholder = 'Your email',
  onJoined,
}) {
  const uid = useId(); // unique ids for the label and the message
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const [subscribe] = useSubscribeProductLaunchMutation();

  async function submit(event) {
    event.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setState('error');
      setError('Enter a valid email address.');
      return;
    }
    setState('sending');
    try {
      await subscribe({ product: 'alpha_ops', email: value }).unwrap();
      setState('idle');
      onJoined(value);
    } catch (err) {
      setState('error');
      setError(subscribeErrorMessage(err));
    }
  }

  const busy = disabled || state === 'sending';
  return (
    <form className="aops-wait" onSubmit={submit} noValidate>
      <label htmlFor={`${uid}-email`} className="aops-sr-only">
        Email address
      </label>
      <input
        id={`${uid}-email`}
        type="email"
        inputMode="email"
        autoComplete="email"
        placeholder={placeholder}
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (state === 'error') setState('idle');
        }}
        disabled={busy}
        aria-invalid={state === 'error'}
        aria-describedby={`${uid}-msg`}
      />
      <button type="submit" disabled={busy}>
        {state === 'sending' ? busyLabel : submitLabel}
      </button>
      <p id={`${uid}-msg`} className={'aops-wait-msg' + (state === 'error' ? ' is-error' : '')} aria-live="polite">
        {state === 'error' ? error : note}
      </p>
    </form>
  );
}
